// useAudioManager.ts
import { useRef, useCallback, useEffect, useMemo, RefObject } from 'react';
import { KernelHandle, useAudioManagerReturnType, Message, SoundEffect, ScriptInstance } from  '#types/types';
import Snd from 'snd-lib';

// UI SOUNDS ARE SND (snd.dev; owner, 2026-09-29: 'install snd.dev', then, hearing it: 'the sounds
// we currently have are perfect', 'dont change them'), replacing uisfx: a designed
// kit of short recorded cues, fetched once from the library's pinned CDN. The app keeps its own
// effect names; each maps to the SND sound that means the same thing, in kit 01 (the soft one).
const SOUND_BY_EFFECT: Partial<Record<SoundEffect, string>> = {
  RESIZE: Snd.SOUNDS.TAP,
  VALUE_TICK: Snd.SOUNDS.TAP,
  TYPE_KEY: Snd.SOUNDS.TYPE,
  SUCCESS: Snd.SOUNDS.CELEBRATION,
  TAB_SWITCH: Snd.SOUNDS.SELECT,
  QUICK_SWITCH: Snd.SOUNDS.TOGGLE_ON,
  CUT: Snd.SOUNDS.SWIPE,
  CONFIRM: Snd.SOUNDS.SELECT,
  INTENSE_NOTIFICATION: Snd.SOUNDS.NOTIFICATION,
  UNDO: Snd.SOUNDS.TRANSITION_DOWN,
  REDO: Snd.SOUNDS.TRANSITION_UP,
  HEATMAP: Snd.SOUNDS.TOGGLE_OFF,
  // Loops while the Daimon browses the web.
  WEB_SEARCH: Snd.SOUNDS.PROGRESS_LOOP,
  // Her click when she drives the app (owner, 2026-10-01; her cursor's travel stays silent, he
  // removed it); her typing is TYPE_KEY.
  DAIMON_CLICK: Snd.SOUNDS.TAP,
};

// Three one-shots the owner picked by ear from uisfx's 'zen' pack (CC0), served from public/sounds:
// its 'start' for a generation launch, its 'wake' when the Daimon connects, and that wake reversed
// when she hangs up (owner, 2026-09-29: 'invert it for the daimon sleep').
const FILE_BY_EFFECT: Partial<Record<SoundEffect, string>> = {
  GENERATE_START: '/sounds/zen-start.mp3',
  VOICE_START: '/sounds/zen-wake.mp3',
  VOICE_END: '/sounds/zen-sleep.mp3',
};

let sndPlayer: Promise<Snd> | null = null;
/** One player for the app, its kit loaded on the first sound; a failed load is retried next time. */
const snd = () => (sndPlayer ??= (async () => {
  const player = new Snd();
  await player.load('01'); // Snd.KITS.SND01, the soft kit
  return player;
})().catch((error) => {
  sndPlayer = null;
  throw error;
}));
const activeSounds: Partial<Record<SoundEffect, { stopTimer?: ReturnType<typeof setTimeout> }>> = {};

interface AudioManagerProps {
  kernel: RefObject<KernelHandle | null>;
}

export const useAudioManager = ({
  kernel
}: AudioManagerProps): useAudioManagerReturnType => {
  const audioQueue = useRef<string[]>([]);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingTTS = useRef(false);

  // This function is internal and handles the actual playback and event listeners
  const fetchAndPlayTTSAudio = useCallback(async (audioUrl: string) => {
    if (ttsAudioRef.current && !ttsAudioRef.current.paused) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.onended = null;
      // Ne pas remettre la ref à null ici : le nouvel objet Audio la remplace juste en dessous.
    }

    const audio = new Audio(audioUrl);
    ttsAudioRef.current = audio; // Assign the new audio object

    return new Promise<void>((resolve, reject) => {
      const cleanupAndResolve = () => {
        isPlayingTTS.current = false;
        document.dispatchEvent(new CustomEvent('tts-audio-ended'));
        resolve();
      };
      const cleanupAndReject = (err: any) => {
        isPlayingTTS.current = false;
        document.dispatchEvent(new CustomEvent('tts-audio-ended'));
        reject(err);
      };

      audio.onended = cleanupAndResolve;
      audio.onerror = (e) => {
        console.error(`Error playing TTS audio from URL ${audioUrl}:`, e);
        cleanupAndReject(e);
      };
      
      audio.play().catch(error => {
        console.error(`Error initiating play for TTS audio from URL ${audioUrl}:`, error);
        cleanupAndReject(error);
      });
    });
  }, []); // No external dependencies that change


  const playNextTTSMessageInQueue = useCallback(async () => {
    if (audioQueue.current.length === 0 || !kernel.current?.getAudioEnabled()) {
      isPlayingTTS.current = false;
      return;
    }
    isPlayingTTS.current = true;
    try {
      const contentUrl = audioQueue.current.shift();
      if (!contentUrl) {
        isPlayingTTS.current = false;
        return;
      }
      await fetchAndPlayTTSAudio(contentUrl);
    } catch (error) {
      console.error('Error in playNextTTSMessageInQueue:', error);
      // isPlayingTTS state is handled by fetchAndPlayTTSAudio
    }
  }, [fetchAndPlayTTSAudio, kernel]);


  useEffect(() => {
    const handleTTSAudioEnded = () => {
      if (audioQueue.current.length > 0 && !isPlayingTTS.current && kernel.current?.getAudioEnabled()) {
        playNextTTSMessageInQueue();
      } else if (audioQueue.current.length === 0) {
        isPlayingTTS.current = false;
      }
    };
    document.addEventListener('tts-audio-ended', handleTTSAudioEnded);
    return () => {
      document.removeEventListener('tts-audio-ended', handleTTSAudioEnded);
    };
  }, [playNextTTSMessageInQueue, kernel]);

  // Renamed to reflect it's for message-associated audio generation
  const generateAudioForChatMessage = useCallback(async (
    content: string,
    messageTimestamp: string,
    playImmediately: boolean = true
  ): Promise<string | undefined> => {
    if (!kernel.current?.getAudioEnabled() || !content.trim()) {
      kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
        msg.timestamp === messageTimestamp && msg.role === 'assistant'
          ? { ...msg, audioUrl: undefined }
          : msg
      ));
      return undefined;
    }

    kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
      msg.timestamp === messageTimestamp && msg.role === 'assistant'
        ? { ...msg, audioUrl: 'waiting' }
        : msg
    ));
    
    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        console.error(`Failed to fetch TTS for chat message (timestamp: ${messageTimestamp}). Status:`, response.status);
        kernel.current?.updateActiveSessionMessages(prev => prev.map(msg => msg.timestamp === messageTimestamp && msg.role === 'assistant' ? { ...msg, audioUrl: undefined } : msg ));
        return undefined;
      }

      const data = await response.json();
      const audioUrl = data.url as string | undefined;

      if (!audioUrl) {
        console.warn(`TTS API did not return an audio URL for chat message (timestamp: ${messageTimestamp}).`);
        kernel.current?.updateActiveSessionMessages(prev => prev.map(msg => msg.timestamp === messageTimestamp && msg.role === 'assistant' ? { ...msg, audioUrl: undefined } : msg ));
        return undefined;
      }
      
      kernel.current?.updateActiveSessionMessages(prev => prev.map(msg =>
        msg.timestamp === messageTimestamp && msg.role === 'assistant'
          ? { ...msg, audioUrl }
          : msg
      ));

      if (playImmediately) {
        // Interrupt and clear queue for immediate playback of this message's audio
        if (ttsAudioRef.current && !ttsAudioRef.current.paused) {
          ttsAudioRef.current.pause();
          ttsAudioRef.current.onended = null;
        }
        ttsAudioRef.current = null;
        audioQueue.current = [];
        
        isPlayingTTS.current = true; // Set before calling fetchAndPlayTTSAudio
        await fetchAndPlayTTSAudio(audioUrl);
      }
      return audioUrl;

    } catch (error) {
      console.error(`Error fetching or playing TTS for chat message (timestamp: ${messageTimestamp}):`, error);
      kernel.current?.updateActiveSessionMessages(prev => prev.map(msg => msg.timestamp === messageTimestamp && msg.role === 'assistant' ? { ...msg, audioUrl: undefined } : msg ));
      return undefined;
    }
  }, [fetchAndPlayTTSAudio, kernel]);

  const addAssistantMessage = useCallback(async (content: string, scriptsInstances?: ScriptInstance[], agentName?: string, imageUrls? : string[], thoughts?: string) => {
    const requestId = new Date().getTime();
    const messageTimestamp = new Date(requestId).toISOString();

    kernel.current?.updateActiveSessionMessages((prevMessages) => {
      const newMessage: Message = {
        content, role: 'assistant', timestamp: messageTimestamp,
        audioUrl: undefined,
        scriptsInstances, agentName, imageUrls,
        thoughts
      };
      return [...prevMessages, newMessage];
    });
  }, [kernel]);

  const playUISound = useCallback((
    effect: SoundEffect,
    options?: { volume?: number; durationMs?: number; loop?: boolean }
  ) => {
    if (!kernel.current?.getAudioEnabled()) return;

    // List projects are SILENT: no generation chimes, success/confirm, key
    // clicks, ticks or resize sounds. The batch/loop experience runs quietly.
    const activeSid = kernel.current?.getActiveChatSessionId?.();
    const activeProjectId = activeSid ? kernel.current?.getLastProjectId?.(activeSid) : null;
    const activeProjectType = activeProjectId
      ? kernel.current?.getInventory?.().Project.registry[activeProjectId]?.projectType
      : null;
    if (activeProjectType === 'LIST') return;

    const volume = Math.max(0, Math.min(1, options?.volume ?? 0.5));
    const durationMs = options?.durationMs;
    // A timed sound (typing for as long as text streams in) is the cue on a loop, stopped on time.
    const loop = Boolean(options?.loop || (durationMs && durationMs > 0));

    const file = FILE_BY_EFFECT[effect];
    if (file) {
      const audio = new Audio(file);
      audio.volume = volume;
      void audio.play().catch(() => undefined);
      return;
    }
    const key = SOUND_BY_EFFECT[effect];
    if (!key) return;
    const previous = activeSounds[effect];
    if (previous?.stopTimer) clearTimeout(previous.stopTimer);
    const entry: { stopTimer?: ReturnType<typeof setTimeout> } = {};
    activeSounds[effect] = entry;
    snd().then((player) => {
      // Stopped while the kit was still loading: nothing to play.
      if (activeSounds[effect] !== entry) return;
      player.play(key, { volume, loop });
      if (durationMs && durationMs > 0) {
        entry.stopTimer = setTimeout(() => {
          if (activeSounds[effect] !== entry) return;
          player.stop(key);
          delete activeSounds[effect];
        }, durationMs);
      } else if (!loop) {
        delete activeSounds[effect];
      }
    }).catch((error) => console.warn('[sound] the SND kit could not load, the app stays silent', error));
  }, [kernel]);

  const stopUISound = useCallback((effect: SoundEffect) => {
    const sound = activeSounds[effect];
    if (!sound) return;
    if (sound.stopTimer) clearTimeout(sound.stopTimer);
    delete activeSounds[effect];
    const key = SOUND_BY_EFFECT[effect];
    if (key) void sndPlayer?.then((player) => player.stop(key)).catch(() => undefined);
  }, []);


  // Public function for playing an arbitrary URL, interrupting current TTS and queue
  const playSpecificAudioUrl = useCallback(async (audioUrl: string) => {
    if (!kernel.current?.getAudioEnabled()) return;
    if (ttsAudioRef.current && !ttsAudioRef.current.paused) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.onended = null;
    }
    ttsAudioRef.current = null;
    audioQueue.current = [];
    isPlayingTTS.current = true; // Set before calling fetchAndPlayTTSAudio
    try {
      await fetchAndPlayTTSAudio(audioUrl);
    } catch (error) {
      console.error(`[AudioManager] Error in playSpecificAudioUrl for ${audioUrl}:`, error);
      // isPlayingTTS state is handled by fetchAndPlayTTSAudio
    }
  }, [fetchAndPlayTTSAudio, kernel]);

  // Public function for generating audio for content associated with a message
  const generateAudioForContent = useCallback(async (content: string, messageTimestamp: string, playImmediately: boolean = true) => {
    await generateAudioForChatMessage(content, messageTimestamp, playImmediately);
  }, [generateAudioForChatMessage]);

  // NEW FUNCTION for ad-hoc alerts
  const playAdhocAlert = useCallback(async (alertText: string) => {
    if (!kernel.current?.getAudioEnabled() || !alertText.trim()) return;

    // Interrupt any ongoing TTS and clear the queue immediately for an alert
    if (ttsAudioRef.current && !ttsAudioRef.current.paused) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.onended = null; // Important to remove listener to prevent queue from advancing
    }
    ttsAudioRef.current = null; // Clear the ref
    audioQueue.current = [];    // Clear the pending queue
    isPlayingTTS.current = false; // Reflect that queue is not active

    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: alertText }),
      });

      if (!response.ok) {
        console.error(`AdhocAlert: Failed to fetch TTS. Status:`, response.status);
        return;
      }
      const data = await response.json();
      const audioUrl = data.url as string | undefined;

      if (audioUrl) {
        isPlayingTTS.current = true; // Set before calling fetchAndPlayTTSAudio
        await fetchAndPlayTTSAudio(audioUrl); // This will play immediately
      } else {
        console.warn(`AdhocAlert: TTS API did not return an audio URL.`);
      }
    } catch (error) {
      console.error('AdhocAlert: Error during TTS generation or playback:', error);
      // isPlayingTTS state is handled by fetchAndPlayTTSAudio's error/ended states
    }
  }, [fetchAndPlayTTSAudio, kernel]); // fetchAndPlayTTSAudio is the core playback utility


  return useMemo(() => ({
    addAssistantMessage,
    playUISound,
    stopUISound,
    playSpecificAudioUrl,
    generateAudioForContent,
    playAdhocAlert
  }), [
    addAssistantMessage,
    playUISound,
    stopUISound,
    playSpecificAudioUrl,
    generateAudioForContent,
    playAdhocAlert
  ]);
};